import { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  loadAnalyticsSettings,
  fetchPageViewStats,
  fetchPageViewCount,
  fetchSessionStats,
  fetchRecentPageViews,
  fetchRecentSessions,
  fetchTopPages,
  type AnalyticsSettings,
  type PageView,
  type VisitorSession,
} from '../lib/analytics';
import AnalyticsOverview from './analytics/AnalyticsOverview';
import PageViewsChart from './analytics/PageViewsChart';
import TopPages from './analytics/TopPages';
import DeviceBreakdown from './analytics/DeviceBreakdown';
import RealtimeFeed from './analytics/RealtimeFeed';
import AnalyticsSettingsPanel from './analytics/AnalyticsSettingsPanel';
import RecentActivity from './analytics/RecentActivity';

const RANGE_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

export default function Analytics() {
  const [settings, setSettings] = useState<AnalyticsSettings | null>(null);
  const [pageViews, setPageViews] = useState<PageView[]>([]);
  const [sessions, setSessions] = useState<VisitorSession[]>([]);
  const [recentViews, setRecentViews] = useState<PageView[]>([]);
  const [recentSessions, setRecentSessions] = useState<VisitorSession[]>([]);
  const [topPages, setTopPages] = useState<{ path: string; views: number }[]>([]);
  const [days, setDays] = useState(30);
  const [dbCount, setDbCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<'overview' | 'realtime' | 'settings'>('overview');

  const load = useCallback(async () => {
    setLoading(true);
    const [s, pv, sess, rpv, rs, tp, dbTotal] = await Promise.all([
      loadAnalyticsSettings(),
      fetchPageViewStats(days),
      fetchSessionStats(days),
      fetchRecentPageViews(50),
      fetchRecentSessions(20),
      fetchTopPages(days, 10),
      fetchPageViewCount(days),
    ]);
    setSettings(s);
    setPageViews(pv as PageView[]);
    setSessions(sess as VisitorSession[]);
    setRecentViews(rpv as PageView[]);
    setRecentSessions(rs as VisitorSession[]);
    setTopPages(tp);
    setDbCount(dbTotal);
    setLoading(false);
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPageViews = pageViews.length;
  const totalSessions = sessions.length;
  const avgPagesPerSession = totalSessions > 0
    ? sessions.reduce((sum, s) => sum + (s.page_count ?? 1), 0) / totalSessions
    : 0;
  const mobileSessions = sessions.filter(s => s.device_type === 'mobile').length;
  const mobilePercent = totalSessions > 0 ? (mobileSessions / totalSessions) * 100 : 0;
  const newVisitors = sessions.filter(s => !s.is_returning).length;
  const newVisitorPercent = totalSessions > 0 ? (newVisitors / totalSessions) * 100 : 0;
  const bouncedSessions = sessions.filter(s => s.page_count === 1).length;
  const bounceRate = totalSessions > 0 ? (bouncedSessions / totalSessions) * 100 : 0;

  const SECTIONS = [
    { key: 'overview', label: 'Overview' },
    { key: 'realtime', label: 'Realtime' },
    { key: 'settings', label: 'Settings' },
  ] as const;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-golf-gray">Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">Built-in visitor tracking &amp; insights</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white text-sm">
            {SECTIONS.map(s => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                className={`px-4 py-2 font-medium transition-colors ${activeSection === s.key ? 'bg-golf-orange text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {activeSection === 'overview' && (
        <>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500 font-medium">Range:</span>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white text-xs">
              {RANGE_OPTIONS.map(o => (
                <button
                  key={o.value}
                  onClick={() => setDays(o.value)}
                  className={`px-3 py-1.5 font-medium transition-colors ${days === o.value ? 'bg-golf-orange text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
            </div>
          ) : (
            <>
              <AnalyticsOverview
                totalPageViews={totalPageViews}
                totalSessions={totalSessions}
                avgPagesPerSession={avgPagesPerSession}
                mobilePercent={mobilePercent}
                newVisitorPercent={newVisitorPercent}
                bounceRate={bounceRate}
              />

              {dbCount !== null && dbCount !== pageViews.length && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
                  <span className="text-amber-700 text-sm font-medium">
                    Database reports {dbCount.toLocaleString()} page views but only {pageViews.length.toLocaleString()} were fetched — some data may be missing due to RLS or connectivity.
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <PageViewsChart pageViews={pageViews} days={days} />
                </div>
                <TopPages pages={topPages} />
              </div>

              <DeviceBreakdown sessions={sessions} />

              <RecentActivity recentViews={recentViews} recentSessions={recentSessions} />
            </>
          )}
        </>
      )}

      {activeSection === 'realtime' && (
        <RealtimeFeed enabled={loading ? false : (settings?.realtime_enabled ?? true)} />
      )}

      {activeSection === 'settings' && settings && (
        <div className="max-w-lg">
          <AnalyticsSettingsPanel
            settings={settings}
            onUpdate={updated => setSettings(updated)}
          />
        </div>
      )}

      {activeSection === 'settings' && !settings && !loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-sm font-medium text-amber-800">Analytics tables not set up yet</p>
          <p className="text-sm text-amber-700 mt-1">
            Please run the analytics setup SQL in your Supabase SQL editor to create the required tables, then refresh this page.
          </p>
        </div>
      )}
    </div>
  );
}
